import { QueryClient } from '@tanstack/react-query';

/**
 * Pre-configured TanStack Query client.
 * - staleTime: 30s (products change infrequently within a session)
 * - refetchOnWindowFocus: true (pick up changes if merchant returns)
 * - retry: 1 (let the error boundary handle hard failures)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Re-export QueryProvider from its own .tsx file
export { QueryProvider } from './QueryProvider';

// ─── App Bridge Session Token ────────────────────────────────

/**
 * Get the Authorization header with the Shopify session token.
 *
 * In the new App Bridge (v4.2+), Shopify Admin injects a script into the
 * embedded app iframe that exposes the `shopify` global on `globalThis`.
 * `shopify.idToken()` returns a signed JWT that the backend verifies.
 *
 * Returns an empty object when running outside Shopify (e.g. local dev).
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shopify = (globalThis as any).shopify;

    console.log('[api:getAuthHeaders] globalThis.shopify exists:', !!shopify);
    console.log('[api:getAuthHeaders] globalThis.shopify type:', typeof shopify);
    if (shopify) {
      console.log('[api:getAuthHeaders] shopify.idToken type:', typeof shopify.idToken);
      console.log('[api:getAuthHeaders] shopify keys:', Object.keys(shopify));
    }

    if (typeof shopify?.idToken === 'function') {
      const token: string = await shopify.idToken();
      console.log('[api:getAuthHeaders] Got session token, length:', token.length, 'first20:', token.substring(0, 20));
      return { Authorization: `Bearer ${token}` };
    }

    console.warn('[api:getAuthHeaders] shopify.idToken is NOT a function — no token will be sent. Running outside Shopify Admin?');
    console.warn('[api:getAuthHeaders] window.location.href:', globalThis.location.href);
    console.warn('[api:getAuthHeaders] document.referrer:', globalThis.document.referrer);
  } catch (err) {
    console.error('[api:getAuthHeaders] Exception getting session token:', err);
  }
  return {};
}

// ─── Authenticated API Client ────────────────────────────────

const API_BASE = '/api';

/**
 * Authenticated fetch wrapper.
 * Injects the Shopify App Bridge session token as a Bearer token
 * in the Authorization header on every request.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const authHeaders = await getAuthHeaders();

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      code: 'UNKNOWN',
      message: response.statusText,
    }));
    throw new ApiError(response.status, error.code ?? 'UNKNOWN', error.message ?? 'Request failed');
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── API Methods ─────────────────────────────────────────────

export const api = {
  get: <T,>(path: string) => apiFetch<T>(path),
  post: <T,>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T,>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),
  delete: <T,>(path: string) =>
    apiFetch<T>(path, { method: 'DELETE' }),
};

// ─── T014: Onboarding & Scan API Endpoints ──────────────────

import type {
  OnboardingResponse,
  OnboardingState,
  UpdateOnboardingRequest,
  TriggerScanRequest,
  ScanProgressExtended,
  ScanResultsResponse,
} from '../types';

/**
 * GET /api/store/onboarding — app entry point.
 */
export function getOnboarding(): Promise<OnboardingResponse> {
  return api.get<OnboardingResponse>('/store/onboarding');
}

/**
 * PATCH /api/store/onboarding — update wizard step.
 */
export function updateOnboardingStep(
  body: UpdateOnboardingRequest,
): Promise<OnboardingState> {
  return api.patch<OnboardingState>('/store/onboarding', body);
}

/**
 * POST /api/scan/store — trigger scan with options.
 */
export function triggerScan(
  body?: TriggerScanRequest,
): Promise<ScanProgressExtended> {
  return api.post<ScanProgressExtended>('/scan/store', body);
}

/**
 * GET /api/store/scan-usage — get monthly scan usage counters.
 */
export interface ScanUsageResponse {
  scansUsed: number;
  scansMax: number;
  isFreePlan: boolean;
}

export function getScanUsage(): Promise<ScanUsageResponse> {
  return api.get<ScanUsageResponse>('/store/scan-usage');
}

/**
 * POST /api/scan/:jobId/cancel — cancel a running scan.
 */
export function cancelScan(
  jobId: string,
): Promise<ScanProgressExtended> {
  return api.post<ScanProgressExtended>(`/scan/${jobId}/cancel`);
}

/**
 * GET /api/scan/:jobId/results — get scan completion results.
 */
export function getScanResults(
  jobId: string,
): Promise<ScanResultsResponse> {
  return api.get<ScanResultsResponse>(`/scan/${jobId}/results`);
}

// ─── Phase 2: Billing API ────────────────────────────────────

import type {
  PlansResponse,
  SubscriptionInfo,
  SubscribeRequest,
  SubscribeResponse,
  ChangeTierRequest,
  ChangeTierResponse,
  CancelResponse,
  CouponRedeemRequest,
  CouponRedeemResponse,
  GeneratePreviewsResponse,
  GetPreviewsResponse,
  UpdatePreviewRequest,
  UpdatePreviewResponse,
  ApplyFixesRequest,
  ApplyFixesResponse,
  FixHistoryDto,
  RevertFixRequest,
  RevertFixResponse,
  ReapplyFixRequest,
  ReapplyFixResponse,
  FeedMetadata,
  ActivateFeedResponse,
  GenerateSummaryRequest,
  SummaryResponse,
  CreateBulkJobRequest,
  CreateBulkJobResponse,
  BulkJobListResponse,
  BulkJobDetailDto,
  BulkApproveRequest,
  BulkApproveResponse,
  CancelBulkJobResponse,
  CategoryTemplate,
  DetectedCategory,
  TierConfigResponse,
} from '../types';

// ─── Config API (public, no auth) ────────────────────────────

/** GET /api/config/tiers — fetch tier limits at app boot */
export function getTierConfig(): Promise<TierConfigResponse> {
  return api.get<TierConfigResponse>('/config/tiers');
}

/** GET /api/billing/plans */
export function getPlans(): Promise<PlansResponse> {
  return api.get<PlansResponse>('/billing/plans');
}

/** GET /api/billing/status */
export function getSubscriptionStatus(): Promise<SubscriptionInfo> {
  return api.get<SubscriptionInfo>('/billing/status');
}

/** POST /api/billing/subscribe */
export function subscribe(body: SubscribeRequest): Promise<SubscribeResponse> {
  return api.post<SubscribeResponse>('/billing/subscribe', body);
}

/** POST /api/billing/change-tier */
export function changeTier(body: ChangeTierRequest): Promise<ChangeTierResponse> {
  return api.post<ChangeTierResponse>('/billing/change-tier', body);
}

/** POST /api/billing/cancel */
export function cancelSubscription(): Promise<CancelResponse> {
  return api.post<CancelResponse>('/billing/cancel');
}

/** POST /api/billing/coupon/redeem */
export function redeemCoupon(body: CouponRedeemRequest): Promise<CouponRedeemResponse> {
  return api.post<CouponRedeemResponse>('/billing/coupon/redeem', body);
}

// ─── Phase 2: Auto-Fix API ──────────────────────────────────

/** POST /api/autofix/preview — generate AI fix previews */
export function generateFixPreviews(productId: string): Promise<GeneratePreviewsResponse> {
  return api.post<GeneratePreviewsResponse>('/autofix/preview', { productId });
}

/** GET /api/autofix/preview/:productId — list previews */
export function getFixPreviews(productId: string): Promise<GetPreviewsResponse> {
  return api.get<GetPreviewsResponse>(`/autofix/preview/${productId}`);
}

/** PATCH /api/autofix/preview/:previewId — approve/reject */
export function updateFixPreview(
  previewId: string,
  body: UpdatePreviewRequest,
): Promise<UpdatePreviewResponse> {
  return api.patch<UpdatePreviewResponse>(`/autofix/preview/${previewId}`, body);
}

/** POST /api/autofix/apply — apply approved fixes */
export function applyFixes(body: ApplyFixesRequest): Promise<ApplyFixesResponse> {
  return api.post<ApplyFixesResponse>('/autofix/apply', body);
}

/** GET /api/autofix/history/:productId — fix history */
export function getFixHistory(productId: string): Promise<FixHistoryDto[]> {
  return api.get<FixHistoryDto[]>(`/autofix/history/${productId}`);
}

/** POST /api/autofix/revert — undo a fix */
export function revertFix(body: RevertFixRequest): Promise<RevertFixResponse> {
  return api.post<RevertFixResponse>('/autofix/revert', body);
}

/** POST /api/autofix/reapply — re-apply a reverted fix */
export function reapplyFix(body: ReapplyFixRequest): Promise<ReapplyFixResponse> {
  return api.post<ReapplyFixResponse>('/autofix/reapply', body);
}

// ─── Phase 2: Feed API ──────────────────────────────────────

/** POST /api/feed/activate — activate product feed */
export function activateFeed(): Promise<ActivateFeedResponse> {
  return api.post<ActivateFeedResponse>('/feed/activate');
}

/** GET /api/feed/status — feed metadata */
export function getFeedStatus(): Promise<FeedMetadata> {
  return api.get<FeedMetadata>('/feed/status');
}

/** POST /api/feed/regenerate — trigger feed regeneration */
export function regenerateFeed(): Promise<{ message: string }> {
  return api.post<{ message: string }>('/feed/regenerate');
}

/** DELETE /api/feed/deactivate — deactivate feed */
export function deactivateFeed(): Promise<{ message: string }> {
  return api.delete<{ message: string }>('/feed/deactivate');
}

/** GET /api/feed/url-check — server-side store domain URL accessibility check */
export interface UrlCheckResult {
  label: string;
  url: string;
  ok: boolean;
  status: number;
}
export interface UrlCheckResponse {
  embedDetected: boolean;
  embedDetail: string;
  checks: UrlCheckResult[];
}
export function checkFeedUrls(): Promise<UrlCheckResponse> {
  return api.get<UrlCheckResponse>('/feed/url-check');
}

/** GET /api/feed/check-product-schema — detect duplicate JSON-LD Product schemas on a product page */
export interface ProductSchemaCheckResponse {
  ok: boolean;
  error?: string;
  totalJsonLd: number;
  productSchemaCount: number;
  zeliumJsonLd: boolean;
  themeJsonLd: boolean;
  hasDuplicate: boolean;
}
export function checkProductSchema(productUrl: string): Promise<ProductSchemaCheckResponse> {
  return api.get<ProductSchemaCheckResponse>(`/feed/check-product-schema?url=${encodeURIComponent(productUrl)}`);
}

/** GET /api/products?page=1&limit=1 — returns the storefront URL of the first product */
export function getFirstProductStorefrontUrl(): Promise<string | null> {
  return api
    .get<{ data: Array<{ storefrontUrl: string | null }> }>('/products?page=1&limit=1')
    .then((res) => res.data?.[0]?.storefrontUrl ?? null);
}

// ─── Phase 2: Summary API ───────────────────────────────────

/** POST /api/summaries/generate — generate AI summaries */
export function generateSummary(body: GenerateSummaryRequest): Promise<SummaryResponse> {
  return api.post<SummaryResponse>('/summaries/generate', body);
}

/** GET /api/summaries/:productId — get product summaries */
export function getSummary(productId: string): Promise<SummaryResponse> {
  return api.get<SummaryResponse>(`/summaries/${productId}`);
}

/** POST /api/summaries/regenerate — regenerate summaries (clears stale flag) */
export function regenerateSummary(body: GenerateSummaryRequest): Promise<SummaryResponse> {
  return api.post<SummaryResponse>('/summaries/regenerate', body);
}

// ─── Phase 2: Bulk Operations API ───────────────────────────

/** POST /api/bulk/jobs — create a bulk job */
export function createBulkJob(body: CreateBulkJobRequest): Promise<CreateBulkJobResponse> {
  return api.post<CreateBulkJobResponse>('/bulk/jobs', body);
}

/** GET /api/bulk/jobs — list bulk jobs */
export function listBulkJobs(): Promise<BulkJobListResponse> {
  return api.get<BulkJobListResponse>('/bulk/jobs');
}

/** GET /api/bulk/jobs/:jobId — get job details */
export function getBulkJobDetail(jobId: string): Promise<BulkJobDetailDto> {
  return api.get<BulkJobDetailDto>(`/bulk/jobs/${jobId}`);
}

/** POST /api/bulk/jobs/:jobId/approve — approve/reject products */
export function approveBulkProducts(
  jobId: string,
  body: BulkApproveRequest,
): Promise<BulkApproveResponse> {
  return api.post<BulkApproveResponse>(`/bulk/jobs/${jobId}/approve`, body);
}

/** POST /api/bulk/jobs/:jobId/cancel — cancel a bulk job */
export function cancelBulkJob(jobId: string): Promise<CancelBulkJobResponse> {
  return api.post<CancelBulkJobResponse>(`/bulk/jobs/${jobId}/cancel`);
}

// ─── Phase 2: Category API ──────────────────────────────────

/** GET /api/categories/templates — list all templates */
export async function getCategoryTemplates(): Promise<CategoryTemplate[]> {
  const res = await api.get<{ templates: CategoryTemplate[]; totalCount: number }>('/categories/templates');
  return res.templates;
}

/** GET /api/categories/templates/:slug — get one template */
export function getCategoryTemplate(slug: string): Promise<CategoryTemplate> {
  return api.get<CategoryTemplate>(`/categories/templates/${slug}`);
}

/** GET /api/categories/detect/:productId — detect category */
export function detectCategory(productId: string): Promise<DetectedCategory> {
  return api.get<DetectedCategory>(`/categories/detect/${productId}`);
}

/** PATCH /api/categories/override/:productId — override category */
export function overrideCategory(
  productId: string,
  body: { categorySlug: string },
): Promise<DetectedCategory> {
  return api.patch<DetectedCategory>(`/categories/override/${productId}`, body);
}

/** PATCH /api/products/:id/attributes — manually write a category attribute metafield */
export function setProductAttribute(
  productId: string,
  key: string,
  value: string,
  label?: string,
): Promise<{ presentAttributes: string[]; attributeValues: Record<string, string> }> {
  return api.patch<{ presentAttributes: string[]; attributeValues: Record<string, string> }>(`/products/${productId}/attributes`, {
    key,
    value,
    ...(label ? { label } : {}),
  });
}

// ─── Phase 2: Storefront Injection API ──────────────────────

/** POST /api/storefront/enable — enable JSON-LD injection + feed link */
export function enableStorefrontInjection(): Promise<StorefrontEnableResponse> {
  return api.post<StorefrontEnableResponse>('/storefront/enable');
}

/** DELETE /api/storefront/disable — disable storefront injection */
export function disableStorefrontInjection(): Promise<{ disabled: boolean; errors: string[] }> {
  return api.delete<{ disabled: boolean; errors: string[] }>('/storefront/disable');
}

/** POST /api/storefront/product/:id/sync — re-sync JSON-LD for one product */
export function syncProductJsonLd(productId: string): Promise<{ success: boolean; errors: string[] }> {
  return api.post<{ success: boolean; errors: string[] }>(`/storefront/product/${productId}/sync`);
}

export interface StorefrontEnableResponse {
  enabled: boolean;
  productsWritten: number;
  productsSkipped: number;
  errors: string[];
}

// ─── Phase 3: AI Crawl Simulation API ───────────────────────

import type {
  RunSimulationResponse,
  CrawlSimulationResult,
  SimulationHistoryItem,
  SimulationMode,
  BenchmarkComparison,
  WebhookSyncStatus,
  StoreSettings,
  UpdateStoreSettingsRequest,
} from '../types';

/** POST /api/simulation/run — run a crawl simulation for a product */
export function runSimulation(
  productId: string,
  mode: SimulationMode = 'QUICK',
): Promise<RunSimulationResponse> {
  return api.post<RunSimulationResponse>('/simulation/run', { productId, mode });
}

/** GET /api/simulation/:productId — get latest simulation for a product */
export function getLatestSimulation(productId: string): Promise<CrawlSimulationResult | null> {
  return api.get<CrawlSimulationResult | null>(`/simulation/${productId}`);
}

/** GET /api/simulation/:productId/history — get simulation history */
export function getSimulationHistory(productId: string): Promise<SimulationHistoryItem[]> {
  return api.get<SimulationHistoryItem[]>(`/simulation/${productId}/history`);
}

// ─── Phase 3: Competitive Benchmarking API ──────────────────

/** GET /api/benchmarks — get benchmark comparison for store */
export function getBenchmarks(): Promise<BenchmarkComparison> {
  return api.get<BenchmarkComparison>('/benchmarks');
}

/** GET /api/benchmarks/opt-out — check opt-out preference */
export function getBenchmarkOptOut(): Promise<{ optedOut: boolean }> {
  return api.get<{ optedOut: boolean }>('/benchmarks/opt-out');
}

/** PATCH /api/benchmarks/opt-out — update opt-out preference */
export function updateBenchmarkOptOut(optOut: boolean): Promise<void> {
  return api.patch<void>('/benchmarks/opt-out', { optOut });
}

// ─── Settings API ──────────────────────────────────────────

/** GET /api/store/settings — fetch store notification & account settings */
export function getStoreSettings(): Promise<StoreSettings> {
  return api.get<StoreSettings>('/store/settings');
}

/** PATCH /api/store/settings — update notification preferences */
export function updateStoreSettings(
  body: UpdateStoreSettingsRequest,
): Promise<StoreSettings> {
  return api.patch<StoreSettings>('/store/settings', body);
}

// ─── WebMCP API ─────────────────────────────────────────────

/** POST /api/storefront/webmcp/enable — enable WebMCP agentic tools (paid tier) */
export function enableWebMcp(): Promise<{ enabled: boolean; errors: string[] }> {
  return api.post<{ enabled: boolean; errors: string[] }>('/storefront/webmcp/enable', {});
}

/** DELETE /api/storefront/webmcp/disable — disable WebMCP agentic tools */
export function disableWebMcp(): Promise<{ disabled: boolean; errors: string[] }> {
  return api.delete<{ disabled: boolean; errors: string[] }>('/storefront/webmcp/disable');
}

// ─── Phase 3: Webhook Real-Time Sync API ────────────────────

/** GET /api/webhook-sync/status — get webhook sync status */
export function getWebhookSyncStatus(): Promise<WebhookSyncStatus> {
  return api.get<WebhookSyncStatus>('/webhook-sync/status');
}

/** POST /api/webhook-sync/enable — enable webhook sync */
export function enableWebhookSync(): Promise<{ enabled: boolean; message: string }> {
  return api.post<{ enabled: boolean; message: string }>('/webhook-sync/enable');
}

/** DELETE /api/webhook-sync/disable — disable webhook sync */
export function disableWebhookSync(): Promise<{ enabled: boolean; message: string }> {
  return api.delete<{ enabled: boolean; message: string }>('/webhook-sync/disable');
}

// ─── Phase 4: AI Crawler Analytics API (F4.3) ───────────────

import type {
  CrawlerAnalyticsStats,
  CrawlerAccessLogsResponse,
} from '../types';

/** GET /api/crawler-analytics/stats — aggregated crawler access stats */
export function getCrawlerAnalyticsStats(days = 30): Promise<CrawlerAnalyticsStats> {
  return api.get<CrawlerAnalyticsStats>(`/crawler-analytics/stats?days=${days}`);
}

/** GET /api/crawler-analytics/logs — recent crawler access logs */
export function getCrawlerAccessLogs(
  limit = 50,
  offset = 0,
): Promise<CrawlerAccessLogsResponse> {
  return api.get<CrawlerAccessLogsResponse>(
    `/crawler-analytics/logs?limit=${limit}&offset=${offset}`,
  );
}
